/**
 * CartContext — global multi-service cart for combined bookings.
 *
 * Each cart item is a snapshot of one bookable service. The cart is persisted
 * in localStorage so users don't lose it on refresh.
 *
 * Supported item types: `daily_tour`, `transfer`.
 * Each item carries the type-specific payload needed by the backend
 * /api/cart/checkout endpoint to create the underlying booking(s).
 */
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

const CartContext = createContext(null);

const STORAGE_KEY = 'bd_cart_v1';

const loadInitial = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(loadInitial);

  // Persist on every change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {
      // ignore quota errors
    }
  }, [items]);

  const addItem = useCallback((item) => {
    // item must include: id (client-side uuid), type, title, date, persons, price, payload
    const withId = { ...item, id: item.id || `${Date.now()}-${Math.random().toString(36).slice(2, 8)}` };
    setItems(prev => [...prev, withId]);
    return withId.id;
  }, []);

  const removeItem = useCallback((id) => {
    setItems(prev => prev.filter(i => i.id !== id));
  }, []);

  const updateItem = useCallback((id, patch) => {
    setItems(prev => prev.map(i => (i.id === id ? { ...i, ...patch } : i)));
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
  }, []);

  const totalAmount = items.reduce((sum, i) => sum + (Number(i.price) || 0), 0);
  const totalItems = items.length;

  return (
    <CartContext.Provider value={{ items, addItem, removeItem, updateItem, clearCart, totalAmount, totalItems }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
};
